import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { AddEditProps, User } from '../models/table-models';
import { Controller, useForm } from 'react-hook-form';
import CommonInputText from '../components/CommonInputText';
import { fetchDistributorDevicesForUser, useAddUser, useGetAllRoles, useGetManagerEligibleAssignees, useUpdateUser } from '../utils/users-hooks';
import React, { useEffect, useState } from 'react';
import { useAsyncResource } from 'use-async-resource';
import { FormMultiSelect, stringsToOptions, stringToOption } from '../components/FormMultiSelect';
import { useGetAllMainGroups } from '../utils/groups-hooks';
import Select, { SingleValue } from 'react-select';
import { MainGroup } from '../models/user-models';
import { DistributorDevice } from '../models/distributor-location-models';
import dateFormat from 'dateformat';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../components/CustomToastContent';
import { toastOptionsTopRight } from '../utils/Enums/ToastOptions';
import { Role } from '../utils/Enums/Roles';
import { FleetUserSection } from '../components/FleetUserSection';

export const AddEditUser = ({ itemToEdit, setShowModal, refetchEntitiesHook }: AddEditProps<User>) => {
    const { t } = useTranslation('translation', { keyPrefix: 'table' });
    const { t: commonT } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: errorT } = useTranslation('translation', { keyPrefix: 'errors' });
    const { t: multiselectT } = useTranslation('translation', { keyPrefix: 'multiselect' });
    const [getAllRoles] = useAsyncResource(useGetAllRoles, []);
    const [getAllMainGroups] = useAsyncResource(useGetAllMainGroups, []);
    const [getEligibleManagerAssignees] = useAsyncResource(useGetManagerEligibleAssignees, []);
    // const [getAllConfigurations] = useAsyncResource(useGetAllConfigurations, []);

    // const maintenanceDaysConfiguration = getAllConfigurations().timerDays.maintenanceDays;

    const mainGroups = getAllMainGroups();
    const eligibleManagerAssignees = getEligibleManagerAssignees().filter(({ id }) => id !== itemToEdit?.id);
    const allGroupsOption = { mainGroup: t('allGroups'), subGroups: [] };
    const defaultMainGroup =
        mainGroups.find((item) => item.mainGroup === itemToEdit?.group?.mainGroup) ?? allGroupsOption;

    const [seenDevices, setSeenDevices] = useState<DistributorDevice[]>([]);

    // Апаратите, от които този човек наистина е пращал локации. Служат за избор, но не се записват
    // сами: сдвояването остава решение на човека, иначе всяко записване на потребител би заличило
    // разминаването, което колоната в таблицата показва.
    useEffect(() => {
        const userId = itemToEdit?.id;
        if (!userId) {
            setSeenDevices([]);
            return;
        }

        let cancelled = false;
        fetchDistributorDevicesForUser(userId)
            .then((result) => {
                if (!cancelled) {
                    setSeenDevices(result);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setSeenDevices([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [itemToEdit?.id]);

    const { control, register, handleSubmit, formState, resetField, watch, getValues, setValue } = useForm<User>({
        mode: 'all',
        defaultValues: {
            ...itemToEdit,
            group: {
                ...itemToEdit?.group,
                mainGroup: defaultMainGroup.mainGroup,
                subGroup: itemToEdit?.group?.subGroup ?? t('allGroups'),
            },
            roles: itemToEdit?.roles,
            assignedUserIds: itemToEdit?.assignedUserIds ?? [],
        },
    });

    const selectedMainGroup = watch('group.mainGroup');
    const selectedRoles = watch('roles');
    const selectedMainGroupOption = mainGroups.find((item) => item.mainGroup === selectedMainGroup);
    const subGroups = [...(selectedMainGroupOption?.subGroups ?? [])].sort((a, b) => a.localeCompare(b));

    const useSubmitHandlerAdd = async (formValue: User) => {
        try {
            await useAddUser(formValue);
            refetchEntitiesHook();
            setShowModal(false);
            toast.success(<CustomToastContent content={[t('userCreated')]} />, toastOptionsTopRight);
        } catch (err: any) {
            if (err.error === 'INVALID_INVOICE_RANGE')
                toast.error(<CustomToastContent content={[errorT('INVALID_INVOICE_RANGE')]} />, toastOptionsTopRight);
            if (err.error === 'INVALID_RECEIPT_RANGE')
                toast.error(<CustomToastContent content={[errorT('INVALID_RECEIPT_RANGE')]} />, toastOptionsTopRight);
        }
    };

    const useSubmitHandlerEdit = async (formValue: User) => {
        if (itemToEdit) {
            formValue.id = itemToEdit.id;
            // if (filterDistributorRoles(formValue.roles).length > 0) formValue = {
            //     ...formValue,
            //     invoiceStartNumber: itemToEdit.invoiceStartNumber,
            //     invoiceEndNumber: itemToEdit.invoiceEndNumber,
            //     invoiceCurrentNumber: itemToEdit.invoiceCurrentNumber,
            //     receiptStartNumber: itemToEdit.receiptStartNumber,
            //     receiptEndNumber: itemToEdit.receiptEndNumber,
            //     receiptCurrentNumber: itemToEdit.receiptCurrentNumber,
            // }
        }

        try {
            await useUpdateUser(formValue);
            refetchEntitiesHook();
            setShowModal(false);
            toast.success(<CustomToastContent content={[t('userUpdated')]} />, toastOptionsTopRight);
        } catch (err: any) {
            if (err.error === 'INVALID_INVOICE_RANGE')
                toast.error(<CustomToastContent content={[errorT('INVALID_INVOICE_RANGE')]} />, toastOptionsTopRight);
            if (err.error === 'INVALID_RECEIPT_RANGE')
                toast.error(<CustomToastContent content={[errorT('INVALID_RECEIPT_RANGE')]} />, toastOptionsTopRight);
        }
    };

    const updateMainGroup = (newValue: SingleValue<MainGroup>, onFieldChange: (arg0?: string) => void) => {
        onFieldChange(newValue?.mainGroup);
        resetField('group.subGroup', { defaultValue: newValue?.mainGroup === t('allGroups') ? t('allGroups') : '' });
    };

    const filterDistributorRoles = (roles: string[] | undefined) => {
        return roles?.filter((role) => [Role.ADMIN, Role.DISTRIBUTOR, Role.INSTALLER].includes(role as any)) ?? [];
    };

    const getDefaultValue = (value?: number | null) => {
        if (value === null || value === undefined) {
            //hasn't saved any value yet
            // return maintenanceDaysConfiguration ? maintenanceDaysConfiguration.toString() : '0';
            return '0';
        }
        //this means that there is already saved value
        return value.toString();
    };

    return (
        <Form
            id='form'
            className='d-flex flex-column align-items-center ps-4 pe-4  w-100'
            noValidate
            onSubmit={itemToEdit ? handleSubmit(useSubmitHandlerEdit) : handleSubmit(useSubmitHandlerAdd)}
        >
            <Form.Group className=' d-flex flex-column mb-2 align-items-start w-100' controlId='username'>
                <CommonInputText
                    label={t('username')}
                    labelTextColor='text-black'
                    type='text'
                    fieldError={formState.errors.username}
                    hookRegister={register('username', {
                        required: {
                            value: true,
                            message: t('username'),
                        },
                    })}
                />
            </Form.Group>

            <div className='d-flex w-100 gap-3 justify-content-around'>
                <Form.Group className=' d-flex flex-column mb-2 align-items-start w-100 ' controlId='password'>
                    <CommonInputText
                        label={t('password')}
                        labelTextColor='text-black'
                        type='password'
                        fieldError={formState.errors.password}
                        hookRegister={register('password', {
                            required: {
                                value: !itemToEdit,
                                message: t('password'),
                            },
                        })}
                    />
                    <span className='hint'>{itemToEdit ? t('passwordNotRequired') : ''}</span>
                </Form.Group>
                <Form.Group className=' d-flex flex-column mb-2 align-items-start w-100 ' controlId='passwordSecond'>
                    <CommonInputText
                        label={t('passwordSecond')}
                        labelTextColor='text-black'
                        type='password'
                        fieldError={formState.errors.passwordSecond}
                        hookRegister={register('passwordSecond', {
                            required: {
                                value: !itemToEdit,
                                message: t('password'),
                            },
                            validate: (value) => value === getValues('password') || String(t('passwordNotMatch')),
                        })}
                    />
                    <span className='hint'>{itemToEdit ? t('passwordNotRequired') : ''}</span>
                </Form.Group>
            </div>

            <Form.Group className=' d-flex flex-column mb-2 align-items-start w-100 ' controlId='name'>
                <CommonInputText
                    label={t('name')}
                    labelTextColor='text-black'
                    type='text'
                    fieldError={formState.errors.name}
                    hookRegister={register('name', {
                        required: {
                            value: true,
                            message: t('name'),
                        },
                    })}
                />
            </Form.Group>
            <div className='d-flex w-100 gap-3 justify-content-around'>
                <Form.Group className=' d-flex flex-column mb-2 align-items-start w-50' controlId='group.mainGroup'>
                    <Form.Label>{t('mainGroup')}</Form.Label>
                    <Controller
                        control={control}
                        name='group.mainGroup'
                        rules={{ required: true }}
                        defaultValue={defaultMainGroup.mainGroup}
                        render={({ field }) => (
                            <>
                                <Select
                                    className='w-100'
                                    value={mainGroups.find((item) => item.mainGroup === field.value) ?? allGroupsOption}
                                    onChange={(newValue) => updateMainGroup(newValue, field.onChange)}
                                    options={[...mainGroups, allGroupsOption]}
                                    getOptionLabel={({ mainGroup }) => mainGroup}
                                    getOptionValue={({ mainGroup }) => mainGroup}
                                />
                                {formState.errors.group?.mainGroup && (
                                    <p className={`input-error`}>{'*' + t('mainGroup')}</p>
                                )}
                            </>
                        )}
                    />
                </Form.Group>
                <Form.Group className=' d-flex flex-column mb-2 align-items-start w-50' controlId='group.subGroup'>
                    <Form.Label>{t('subGroup')}</Form.Label>
                    <Controller
                        control={control}
                        name='group.subGroup'
                        defaultValue={itemToEdit?.group?.subGroup ?? t('allGroups')}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <>
                                <Select
                                    className='w-100'
                                    value={
                                        stringToOption(field.value).value === ' '
                                            ? { value: '', name: t('allGroups') }
                                            : stringToOption(field.value)
                                    }
                                    onChange={(newValue) => field.onChange(newValue?.value)}
                                    options={[...stringsToOptions(subGroups), { value: ' ', name: t('allGroups') }]}
                                    getOptionLabel={({ name }) => name}
                                    getOptionValue={({ value }) => value}
                                />
                                {formState.errors.group?.subGroup && (
                                    <p className={`input-error`}>{'*' + t('subGroup')}</p>
                                )}
                            </>
                        )}
                    />
                </Form.Group>
            </div>

            <div className='d-flex w-100 gap-3 justify-content-around'>
                <Form.Group className=' d-flex flex-column mb-2 align-items-start w-100 ' controlId='town'>
                    <CommonInputText
                        label={t('town')}
                        labelTextColor='text-black'
                        type='text'
                        fieldError={formState.errors.town}
                        hookRegister={register('town', {
                            required: {
                                value: false,
                                message: t('town'),
                            },
                        })}
                    />
                </Form.Group>
                <Form.Group className=' d-flex flex-column mb-2 align-items-start w-100 ' controlId='phone'>
                    <CommonInputText
                        label={t('phone')}
                        labelTextColor='text-black'
                        type='text'
                        fieldError={formState.errors.phone}
                        hookRegister={register('phone', {
                            required: {
                                value: true,
                                message: t('phone'),
                            },
                        })}
                    />
                </Form.Group>

                <Form.Group className=' d-flex flex-column mb-2 align-items-start w-100 ' controlId='email'>
                    <CommonInputText
                        label={t('email')}
                        labelTextColor='text-black'
                        type='email'
                        fieldError={formState.errors.email}
                        hookRegister={register('email', {
                            required: {
                                value: true,
                                message: t('email'),
                            },
                        })}
                    />
                </Form.Group>
            </div>

            <Form.Group
                className=' d-flex flex-column mb-2 align-items-start w-100 '
                controlId='mobileDeviceId'
            >
                <div className='d-flex w-100 gap-2 align-items-end'>
                    <div className='flex-grow-1'>
                        <CommonInputText
                            label={t('mobileDevice')}
                            defaultValue={itemToEdit?.mobileDeviceId}
                            placeHolder={seenDevices[0]?.mobileDeviceId}
                            labelTextColor='text-black'
                            type='text'
                            fieldError={formState.errors.mobileDeviceId}
                            hookRegister={register('mobileDeviceId', {
                                maxLength: 64,
                            })}
                        />
                    </div>
                    {seenDevices.length > 0 && (
                        <select
                            className='form-select font-monospace'
                            style={{ maxWidth: 240 }}
                            value=''
                            onChange={(event) => setValue('mobileDeviceId', event.target.value)}
                        >
                            <option value=''>{t('seenDevices')}</option>
                            {seenDevices.map((device) => (
                                <option key={device.mobileDeviceId} value={device.mobileDeviceId}>
                                    {device.mobileDeviceId} ({dateFormat(device.lastSeenAt, 'dd/mm/yyyy')})
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </Form.Group>

            {itemToEdit?.id && selectedRoles?.some((role) => [Role.DISTRIBUTOR, Role.INSTALLER, Role.MANAGER].includes(role as any)) && (
                <FleetUserSection userId={itemToEdit.id} userName={itemToEdit.name} />
            )}

            <Form.Group className=' d-flex flex-column mb-2 align-items-start w-100 ' controlId='comment'>
                <CommonInputText
                    label={t('comment')}
                    labelTextColor='text-black'
                    type='text'
                    fieldError={formState.errors.comment}
                    hookRegister={register('comment', {})}
                />
            </Form.Group>

            <div className='d-flex w-100 gap-3 justify-content-around'>
                <Form.Group className=' d-flex flex-column mb-2 align-items-start w-100 ' controlId='roles'>
                    <Controller
                        control={control}
                        name='roles'
                        defaultValue={itemToEdit?.roles}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <>
                                <FormMultiSelect
                                    value={stringsToOptions(field.value)}
                                    setValue={field.onChange}
                                    options={stringsToOptions(getAllRoles())}
                                    label={t('roles')}
                                />
                                {formState.errors.roles && <p className={`input-error`}>{'*' + t('roles')}</p>}
                            </>
                        )}
                    />
                </Form.Group>
                {filterDistributorRoles(selectedRoles).length > 0 && (
                    <Form.Group
                        className=' d-flex flex-column mb-2 align-items-start w-100 '
                        controlId='maintenancedays'
                    >
                        <CommonInputText
                            label={commonT('maintenanceDays')}
                            defaultValue={getDefaultValue(itemToEdit?.maintenancedays)}
                            labelTextColor='text-black'
                            type='number'
                            fieldError={formState.errors.maintenancedays}
                            hookRegister={register('maintenancedays', {
                                required: {
                                    value:
                                        selectedRoles.includes(Role.DISTRIBUTOR) &&
                                        (itemToEdit === null ? true : itemToEdit?.maintenancedays === null),
                                    message: commonT('maintenanceDays'),
                                },
                                min: 0,
                            })}
                        />
                        {/*    <UserDocumentDetailsForm {...{ formState, register, itemToEdit, roles: watch('roles') }} />}*/}
                    </Form.Group>
                )}

                {selectedRoles?.includes(Role.MANAGER) && (
                    <Form.Group className='d-flex flex-column mb-2 align-items-start w-100' controlId='assignedUserIds'>
                        <Controller
                            control={control}
                            name='assignedUserIds'
                            defaultValue={itemToEdit?.assignedUserIds ?? []}
                            render={({ field }) => (
                                <>
                                    <Form.Label>{t('managerAssignees')}</Form.Label>
                                    <Select
                                        className='w-100'
                                        value={eligibleManagerAssignees.filter(({ id }) => field.value?.includes(id))}
                                        onChange={(values) => field.onChange(values.map(({ id }) => id))}
                                        options={eligibleManagerAssignees}
                                        isMulti
                                        isClearable
                                        closeMenuOnSelect={false}
                                        getOptionLabel={({ name, roles, groupName }) =>
                                            `${name} (${roles.map((role) => multiselectT(role)).join(', ')})${groupName ? ` — ${groupName}` : ''
                                            }`
                                        }
                                        getOptionValue={({ id }) => id.toString()}
                                        placeholder={t('managerAssigneesPlaceholder')}
                                    />
                                </>
                            )}
                        />
                    </Form.Group>
                )}
            </div>
        </Form>
    );
};
